/**
 * Chart Styling Constants
 *
 * Unified styling constants for all analytics charts.
 * Ensures visual consistency across the entire analytics system.
 *
 * Usage:
 * - Import these constants instead of hardcoding values
 * - All charts should use these shared values for consistency
 */

// =============================================================================
// Bar Chart Styling
// =============================================================================

/**
 * Standard border radius for vertical bar charts.
 * Rounded top corners only: [topLeft, topRight, bottomRight, bottomLeft]
 * Sharp corners (2px) for a professional look.
 */
export const BAR_RADIUS_VERTICAL: [number, number, number, number] = [2, 2, 0, 0];

/**
 * Standard border radius for horizontal bar charts.
 * Rounded right corners only: [topLeft, topRight, bottomRight, bottomLeft]
 * Sharp corners (2px) for a professional look.
 */
export const BAR_RADIUS_HORIZONTAL: [number, number, number, number] = [0, 2, 2, 0];

/**
 * Standard maximum bar size for all bar charts.
 * Ensures bars don't get too wide on large screens.
 */
export const BAR_MAX_SIZE = 24;

// =============================================================================
// Chart Colors
// =============================================================================

/**
 * Primary chart color using CSS variable.
 * Used for single-color charts like visits and day-of-week.
 */
export const CHART_COLOR_PRIMARY = 'var(--chart-1)';

/**
 * Chart color palette using CSS variables.
 * Use for multi-category charts to ensure consistent coloring.
 */
export const CHART_COLORS = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
] as const;

/**
 * Get a chart color by index (cycles through palette).
 * @param index - The index of the item
 * @returns CSS variable string for the color
 */
export function getChartColor(index: number): string {
  return CHART_COLORS[index % CHART_COLORS.length] ?? CHART_COLORS[0];
}

/**
 * HSL chart colors for components that need hsl() format.
 * Some CSS properties require the hsl() wrapper.
 */
export const CHART_COLORS_HSL = [
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
] as const;

/**
 * Get an HSL chart color by index (cycles through palette).
 * @param index - The index of the item
 * @returns HSL CSS string for the color
 */
export function getChartColorHsl(index: number): string {
  return CHART_COLORS_HSL[index % CHART_COLORS_HSL.length] ?? CHART_COLORS_HSL[0];
}

// =============================================================================
// Chart Dimensions
// =============================================================================

/**
 * Standard chart heights for consistency.
 */
export const CHART_HEIGHTS = {
  /** Small charts like day-of-week */
  small: 128,
  /** Medium charts like visits */
  medium: 160,
  /** Large charts */
  large: 200,
} as const;

/**
 * Standard chart margins.
 */
export const CHART_MARGINS = {
  /** Default margins for vertical bar charts */
  vertical: { top: 4, right: 4, bottom: 0, left: 4 },
  /** Default margins for horizontal bar charts */
  horizontal: { top: 0, right: 16, bottom: 0, left: 0 },
} as const;

// =============================================================================
// Axis Styling
// =============================================================================

/**
 * Standard font size for axis labels.
 */
export const AXIS_FONT_SIZE = 11;

/**
 * Standard Y-axis width for horizontal bar charts.
 */
export const Y_AXIS_WIDTH = 80;

// =============================================================================
// Tooltip Styling
// =============================================================================

/**
 * Standard tooltip indicator size.
 */
export const TOOLTIP_INDICATOR_SIZE = 'h-2.5 w-2.5 rounded-sm';

// =============================================================================
// Progress Bar Styling
// =============================================================================

/**
 * Standard height for progress bars (custom charts).
 */
export const PROGRESS_BAR_HEIGHT = 'h-2';

/**
 * Standard border radius for progress bars.
 */
export const PROGRESS_BAR_RADIUS = 'rounded-sm';

// =============================================================================
// Heatmap Styling
// =============================================================================

/**
 * Raw HSL values for heatmap (amber/orange to match chart theme).
 * Using raw values because CSS `hsl(var(...) / opacity)` requires
 * the variable to NOT include `hsl()` wrapper, but our theme uses full values.
 */
export const HEATMAP_HSL = '38 92% 50%';

/**
 * Get heatmap color with specific intensity/opacity.
 * @param intensity - Opacity value from 0 to 1
 * @returns CSS color string with opacity
 */
export function getHeatmapColor(intensity: number): string {
  return `hsl(${HEATMAP_HSL} / ${intensity})`;
}

/**
 * Heatmap empty cell color.
 */
export const HEATMAP_EMPTY_COLOR = 'hsl(0 0% 50% / 0.15)';
